import { useEffect, useState } from 'react';
import { Grid, Radio, Checkbox, Error, Legend, ClassHelper } from 'components/lib';
import Style from './fieldset.tailwind.js';

interface FieldsetOptionObject {
  value?: string;
  name?: string;
}
type FieldsetOption = string & Partial<FieldsetOptionObject>;

interface FieldsetProps {
  type?: 'radio' | 'checkbox' | string;
  options: FieldsetOption[];
  valid?: boolean;
  label?: string;
  selectAll?: { value?: string; label?: string; unselectLabel?: string };
  value?: FieldsetOption | FieldsetOption[];
  default?: FieldsetOption | FieldsetOption[];
  errorMessage?: string;
  required?: boolean;
  onChange: (name: string | undefined, value: unknown, valid?: boolean) => void;
  min?: number;
  max?: number;
  className?: string;
  width?: number | string;
  cols?: number;
  name: string;
}

export function Fieldset(props: FieldsetProps) {
  // init
  const Input = props.type === 'radio' ? Radio : Checkbox;
  const options = props.options || [];
  let init = new Array(options.length).fill(false);
  const valid = props.valid === false ? false : true;
  const isInlineCheckbox = props.type === 'checkbox' && props.options.length === 1 && props.label && !props.selectAll;
  const showSelectAll = props.type === 'checkbox' && props.selectAll;

  function optionValueOf(opt: unknown): unknown {
    return (opt && typeof opt === 'object' ? (opt as FieldsetOptionObject).value : undefined) ?? opt;
  }

  function getOptionIndex(option: unknown) {
    // Allow falsy-but-valid values like '' for single inline checkboxes (e.g. accept terms)
    if (option === undefined || option === null) return -1;
    const optionValue = optionValueOf(option);
    return options.findIndex((opt) => optionValueOf(opt) === optionValue);
  }

  function buildChecked() {
    const next = new Array(options.length).fill(false);

    if (props.type === 'radio') {
      const source = props.value ?? props.default;
      const index = getOptionIndex(source ?? 0);
      if (index >= 0) next[index] = true;
      return next;
    }

    if (props.type === 'checkbox') {
      const source = Array.isArray(props.value) ? props.value : Array.isArray(props.default) ? props.default : [];
      source.forEach((opt) => {
        const index = getOptionIndex(opt);
        if (index >= 0) next[index] = true;
      });
    }

    return next;
  }

  // default
  init = buildChecked();

  // state
  const [checked, setChecked] = useState([...init]);
  const [error, setError] = useState(props.errorMessage ? props.errorMessage : 'Please select an option');
  const allSelected = checked.length > 0 && checked.every(Boolean);
  const anySelected = checked.some(Boolean);
  const selectAllLabel = allSelected
    ? props.selectAll?.unselectLabel || 'UNSELECT ALL'
    : props.selectAll?.label || 'SELECT ALL';

  useEffect(() => {
    setChecked(buildChecked());
  }, [props.value, props.default, options, props.type]);

  if (!options.length) return false;

  function change(index: number, _state: boolean | undefined, option?: unknown) {
    // update state
    let data = [...checked];

    // reset radios on select
    if (props.type === 'radio') data = new Array(props.options.length).fill(false);

    // toggle the checked state
    data[index] = !checked[index];

    // update & validate
    setChecked(data);
    validate(data, option as FieldsetOption | undefined);
  }

  function toggleAll() {
    const next = checked.filter(Boolean).length !== props.options.length;
    const data = new Array(props.options.length).fill(next);
    setChecked(data);
    validate(data);
  }

  function validate(data: boolean[], option?: FieldsetOption) {
    let options: FieldsetOption[] = [];
    let errorMessage: string | undefined;
    let valid: boolean | number | undefined = undefined;
    let count = data.filter(Boolean).length;

    // validate radio
    if (props.type === 'radio') {
      valid = props.required ? count : true;
      props.onChange(props.name, option, valid as boolean);
      return;
    }

    // validate checkbox
    if (props.type === 'checkbox') {
      if (props.min && !props.max) {
        // check for min value
        if (count >= props.min) {
          valid = true;
        } else {
          valid = false;
          errorMessage = `Please select at least ${props.min} option`;
          if (props.min > 1) errorMessage += 's';
          setError(errorMessage);
        }
      } else if (!props.min && props.max) {
        // check for max value
        if (count <= props.max) {
          valid = true;
        } else {
          valid = false;
          errorMessage = `Please select a maximum of ${props.max} option`;
          if (props.max > 1) errorMessage += 's';
          setError(errorMessage);
        }
      } else if (props.min && props.max) {
        // check for min and max value
        if (count >= props.min && count <= props.max) {
          valid = true;
        } else {
          valid = false;
          setError(`Please choose between ${props.min} and ${props.max} options`);
        }
      } else if (props.required) {
        valid = count ? true : false;
      }
    }

    data.forEach((opt, index) => {
      if (opt) options.push(props.options[index]);
    });

    // update the parent form state
    props.onChange(props.name, options, valid as boolean | undefined);
  }

  // style
  const fieldsetStyle = ClassHelper(Style, {
    error: valid === false,
    className: props.className,
  });

  return (
    <fieldset
      className={fieldsetStyle}
      // it was added by Huseyin
      style={{ width: props.width, paddingRight: '10px', paddingLeft: '10px' }}
    >
      {isInlineCheckbox ? (
        <div className={Style.inlineRow}>
          <div className={Style.inlineCheckbox}>
            <Input
              name={props.options[0]?.value || props.name}
              index={0}
              option={props.options[0]?.name || props.options[0]}
              checked={checked[0]}
              callback={change}
              className={Style.inlineInput}
            />
          </div>
          <Legend valid={valid} text={props.label} required={props.required} className={Style.inlineLegend} />
        </div>
      ) : (
        <>
          <Legend valid={valid} text={props.label} required={props.required} />

          {showSelectAll && (
            <div className={Style.selectAllRow}>
              <Input
                name={props.selectAll?.value || props.name}
                index={-1}
                option={props.selectAll?.value || `${props.name}-select-all`}
                id={`${props.name}-select-all`}
                labelText={selectAllLabel}
                checked={allSelected}
                indeterminate={anySelected && !allSelected}
                callback={toggleAll}
              />
            </div>
          )}

          {/* cols={props.cols} was added by Huseyin */}
          <Grid cols={props.cols || 3}>
            {options.map((option, index) => {
              return (
                <Input
                  key={index}
                  name={props.name}
                  index={index}
                  option={option.name || option}
                  checked={checked[index]}
                  callback={change}
                />
              );
            })}
          </Grid>
        </>
      )}

      {!valid && <Error message={error} className={Style.errorMessage} />}
    </fieldset>
  );
}
